import {
  Activity,
  Boxes,
  Building2,
  CheckSquare,
  Coins,
  Container,
  FolderGit2,
  FolderKanban,
  Gauge,
  GitBranch,
  Grid2x2,
  Handshake,
  KeyRound,
  LayoutDashboard,
  ListTodo,
  MessageSquareHeart,
  MessagesSquare,
  Network,
  PiggyBank,
  ScrollText,
  Settings,
  TriangleAlert,
  Users,
  UsersRound,
  Webhook,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import type { Key } from '../../i18n/provider';

/**
 * Las secciones de una empresa, que son la navegación principal del producto.
 *
 * Vivían dentro de `CompanyLayout` como una fila de veintiuna pestañas con
 * scroll horizontal, y el comentario que las acompañaba ya reconocía el
 * problema: «veintiuna pestañas en una sola fila eran una lista, no una
 * navegación». Ahora son la barra lateral, y por eso viven aquí: la barra las
 * necesita, y `CompanyLayout` ya no.
 *
 * Los grupos siguen siendo los mismos cuatro —mirar, organizar, trabajar,
 * configurar— y las etiquetas siguen siendo las mismas claves. Eso no es
 * pereza: los flujos de extremo a extremo pinchan estos enlaces por su nombre
 * visible, así que renombrarlos de paso habría convertido un cambio de
 * navegación en un cambio de contrato con los tests.
 */
export interface Section {
  /** El camino de la ruta, con `$companySlug` sin sustituir. */
  to: string;
  label: Key;
  icon: LucideIcon;
  /**
   * Solo el panel compara el camino entero.
   *
   * Sin esto todas las entradas parecerían activas a la vez, porque cada uno de
   * sus caminos empieza por el del panel.
   */
  exact: boolean;
}

export interface SectionGroup {
  label: Key;
  sections: Section[];
}

export const COMPANY_SECTIONS: SectionGroup[] = [
  {
    label: 'company.sections.overview',
    sections: [
      { to: '/c/$companySlug', label: 'company.tabs.panel', icon: LayoutDashboard, exact: true },
      { to: '/c/$companySlug/costs', label: 'company.tabs.costs', icon: Coins, exact: false },
      { to: '/c/$companySlug/audit', label: 'company.tabs.audit', icon: ScrollText, exact: false },
      /*
       * La vigésima quinta, añadida el 2026-09-13: la consola de solo lectura
       * del clúster de la empresa. Es observación, como los costes y la
       * auditoría; las acciones viven en la pestaña Kubernetes de cada proyecto.
       */
      { to: '/c/$companySlug/cluster', label: 'company.tabs.cluster', icon: Boxes, exact: false },
    ],
  },
  {
    label: 'company.sections.organization',
    sections: [
      { to: '/c/$companySlug/org', label: 'company.tabs.org', icon: Network, exact: false },
      { to: '/c/$companySlug/employees', label: 'company.tabs.employees', icon: Users, exact: false },
      { to: '/c/$companySlug/departments', label: 'company.tabs.departments', icon: Building2, exact: false },
      { to: '/c/$companySlug/teams', label: 'company.tabs.teams', icon: UsersRound, exact: false },
      { to: '/c/$companySlug/permissions', label: 'company.tabs.permissions', icon: Grid2x2, exact: false },
    ],
  },
  {
    label: 'company.sections.work',
    sections: [
      { to: '/c/$companySlug/projects', label: 'company.tabs.projects', icon: FolderKanban, exact: false },
      { to: '/c/$companySlug/tasks', label: 'company.tabs.tasks', icon: ListTodo, exact: false },
      /*
       * La vigésima cuarta, añadida el 2026-09-08: el feedback de las apps que
       * espera una decisión, de todos los proyectos de la empresa a la vez. La
       * pestaña de cada proyecto sigue enseñando su historial completo.
       */
      { to: '/c/$companySlug/feedback', label: 'company.tabs.feedback', icon: MessageSquareHeart, exact: false },
      /*
       * La vigésima segunda, añadida el 2026-09-01: las compuertas paran tareas
       * *de esta empresa*, y su entrada de menú vivía solo fuera, en el espacio
       * de trabajo. La bandeja global de `13 §3.8` sigue allí —es personal,
       * cruza empresas—; esta enseña las de la empresa por la que se navega.
       */
      { to: '/c/$companySlug/approvals', label: 'company.tabs.approvals', icon: CheckSquare, exact: false },
      { to: '/c/$companySlug/runs', label: 'company.tabs.runs', icon: Activity, exact: false },
      { to: '/c/$companySlug/conversations', label: 'company.tabs.conversations', icon: MessagesSquare, exact: false },
      { to: '/c/$companySlug/agreements', label: 'company.tabs.agreements', icon: Handshake, exact: false },
    ],
  },
  {
    label: 'company.sections.settings',
    sections: [
      { to: '/c/$companySlug/settings/general', label: 'company.tabs.general', icon: Settings, exact: false },
      { to: '/c/$companySlug/settings/credentials', label: 'company.tabs.credentials', icon: KeyRound, exact: false },
      { to: '/c/$companySlug/settings/autonomy', label: 'company.tabs.autonomy', icon: Gauge, exact: false },
      { to: '/c/$companySlug/settings/budgets', label: 'company.tabs.budgets', icon: PiggyBank, exact: false },
      { to: '/c/$companySlug/settings/jenkins', label: 'company.tabs.jenkins', icon: Settings, exact: false },
      { to: '/c/$companySlug/settings/kubernetes', label: 'company.tabs.kubernetes', icon: Container, exact: false },
      { to: '/c/$companySlug/settings/github', label: 'company.tabs.github', icon: GitBranch, exact: false },
      { to: '/c/$companySlug/settings/repos', label: 'company.tabs.repos', icon: FolderGit2, exact: false },
      { to: '/c/$companySlug/settings/notifications', label: 'company.tabs.webhooks', icon: Webhook, exact: false },
      { to: '/c/$companySlug/settings/danger', label: 'company.tabs.danger', icon: TriangleAlert, exact: false },
    ],
  },
];

/** ¿Está esta sección activa para el camino en el que estamos? */
export function isSectionActive(section: Section, pathname: string, companySlug: string): boolean {
  const path = section.to.replace('$companySlug', companySlug);

  return section.exact ? pathname === path : pathname.startsWith(path);
}

/**
 * La sección en la que estamos, que es lo que la cabecera anuncia cuando la
 * barra está plegada.
 *
 * Recorre al revés porque los caminos anidados —`/settings/general` dentro de
 * `/settings`— tienen que ganar al más corto, y el más corto siempre va antes
 * en la lista.
 */
export function findSection(pathname: string, companySlug: string): Section | undefined {
  const all = COMPANY_SECTIONS.flatMap((group) => group.sections);
  const nested = [...all].reverse().find((section) => !section.exact && isSectionActive(section, pathname, companySlug));

  return nested ?? all.find((section) => isSectionActive(section, pathname, companySlug));
}
